import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { TFunction } from 'i18next';
import { type ReactElement, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';

import type { ChannelCopyState, HomeChannel } from '../../api';
import { useLanguage } from '../../app/i18n';
import { ChannelAvatar } from '../../components/ChannelAvatar';
import { SectionHeader } from '../../components/SectionHeader';
import { StatusChip, type StatusTone } from '../../components/StatusChip';
import type { SupportedLanguage } from '../../i18n';
import { isHiddenMode, useFeatures } from '../../lib/config/features-context';
import { channelSettingLabel } from '../shared/channel-setting-label';

import { homeLinks } from './links';
import { rowDividerSx, sectionSx } from './styles';

// C3 R12: the copy state to tone mapping lives in the feature (mockups Main, AccueilBloque).
const COPY_STATE_TONE: Record<ChannelCopyState, StatusTone> = {
  active: 'success',
  paused: 'neutral',
  to_configure: 'warning',
  suspended_limit: 'warning',
  suspended_external: 'danger',
};

/** The setting summary line and the P8 hint under it share their size. */
const SETTING_LINE_SX = { display: 'block', fontSize: 13 } as const;

/**
 * Chosen channels with their copy state and setting summary (FXM-02 §5 "Channel"; mockup 1).
 * A channel that is not set up says it copies nothing (FXM-02 §7; M1 R8, AC3).
 */
export function ChannelList({
  channels,
}: {
  readonly channels: readonly HomeChannel[];
}): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  const features = useFeatures();
  const titleId = useId();
  return (
    <Box component="section" aria-labelledby={titleId} sx={sectionSx}>
      <SectionHeader
        id={titleId}
        title={t('home.channels.title')}
        action={
          <Link component={RouterLink} to={homeLinks.channels}>
            {t('home.channels.manage')}
          </Link>
        }
      />
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {channels.length === 0 ? (
          <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>
            {t('home.channels.empty')}
          </Typography>
        ) : (
          <List disablePadding>
            {channels.map((channel) => (
              <ListItem key={channel.subscriptionId} disablePadding sx={rowDividerSx}>
                <ListItemButton
                  component={RouterLink}
                  to={homeLinks.channelSettings(channel.subscriptionId)}
                  sx={{ gap: 1.5, py: 1.5, px: 1.75 }}
                >
                  <ChannelAvatar seed={channel.subscriptionId} name={channel.channelName} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {channel.channelName}
                    </Box>
                    <Box
                      component="span"
                      sx={(theme) => ({
                        ...SETTING_LINE_SX,
                        color: channel.setting === null ? theme.fx.warning : theme.fx.textMuted,
                      })}
                    >
                      {settingLabel(channel, t, language)}
                    </Box>
                    {/* P8 R6 (Proposal, to be validated): saved in a mode that is hidden for now. */}
                    {channel.setting !== null &&
                      isHiddenMode(channel.setting.executionMode, features) && (
                        <Box
                          component="span"
                          sx={(theme) => ({ ...SETTING_LINE_SX, color: theme.fx.warning })}
                        >
                          {t('home.channels.confirmationUnavailableHint')}
                        </Box>
                      )}
                  </Box>
                  <StatusChip tone={COPY_STATE_TONE[channel.copyState]}>
                    {t(`home.channels.copyState.${channel.copyState}`)}
                  </StatusChip>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
}

/** "Risk 1 % · Automatic", "Fixed lot 0.05 · Confirmation", or the not-set-up hint. */
function settingLabel(channel: HomeChannel, t: TFunction, language: SupportedLanguage): string {
  if (channel.setting === null) return t('home.channels.toConfigureHint');
  return channelSettingLabel(channel.setting, t, language);
}
